---
title: Introduction
description: Accessible audio controls for React, as headless primitives on npm and as styled components you copy into your shadcn/ui project. Built to be used by people and by AI agents.
---

**audio/ui** is a set of accessible, composable audio controls for React: knobs, faders, XY pads, envelopes, meters, waveforms, timelines, sequencers, drum pads, piano keyboards and piano rolls.


Audio controls have many details: a knob has to drag, respond to the wheel and the keyboard, reset on double click, and be announced correctly by a screen reader. audio/ui implements them once.

## Two layers

Use the layer that fits your project. They share the same behavior.

**`@audio-ui/react`** are headless primitives from npm: behavior and accessibility, no styles. Pick them when you have your own design system.

```bash
npm install @audio-ui/react
```

**The `@audio-ui` registry** holds styled components and ready blocks that you copy into your code and own. Pick it when you use shadcn/ui and Tailwind CSS v4.

```bash
npx shadcn@latest add @audio-ui/knob
```

Both are open source. With the registry you receive the actual code, so you can read it, change it and learn from it.

## Top components

Each links to its docs, with a live preview, the API and the source. Install any of them with `npx shadcn@latest add @audio-ui/<name>`.

<TypeTable title="Top components">
<TypeRow name="Knob">Rotary parameter with drag, wheel, keyboard and reset. [Docs](/docs/components/base/knob).</TypeRow>
<TypeRow name="Fader">Linear gain or parameter slider. [Docs](/docs/components/base/fader).</TypeRow>
<TypeRow name="XY Pad">Two parameters on one surface. [Docs](/docs/components/base/xypad).</TypeRow>
<TypeRow name="Envelope">Breakpoint editor for ADSR and other ramps. [Docs](/docs/components/base/envelope).</TypeRow>
<TypeRow name="Meter">Stereo and multi-channel level with peak hold. [Docs](/docs/components/base/meter).</TypeRow>
<TypeRow name="Waveform">Seekable, live or bar-style waveform with regions. [Docs](/docs/components/base/waveform).</TypeRow>
<TypeRow name="Plot">Draggable curves for EQs, filters and compressors. [Docs](/docs/components/base/plot).</TypeRow>
<TypeRow name="Timeline">Zoomable arrangement view with a smooth playhead. [Docs](/docs/components/base/arrangement).</TypeRow>
<TypeRow name="Sequencer">Step grid to draw a pattern. [Docs](/docs/components/base/sequencer).</TypeRow>
<TypeRow name="Pad">Drum pads with velocity and choke groups. [Docs](/docs/components/base/pad).</TypeRow>
<TypeRow name="Piano Roll">DAW-style note editor with a velocity lane. [Docs](/docs/components/base/piano-roll).</TypeRow>
</TypeTable>

Looking for a whole screen? The [component catalog](/components) has composed blocks (a pocket synth, a wave shaper, a waveform player) you can install the same way.

## Built for agents

audio/ui is made to be used by AI coding agents as much as by people. Open code, one consistent API and plain markdown docs mean an agent can find the right component, read how it works and wire it up without guessing.

- **[Agent skill](/docs/agents):** teaches your agent which layer to pick, how to install and the mistakes to avoid.
- **[MCP server](/docs/mcp):** `https://www.audio-ui.dev/mcp` lets your agent list components, read their docs and search the catalog.
- **[llms.txt](/llms.txt):** a structured map of every docs page, each also served as markdown.

## Principles

- **Accessible by default:** native inputs where it matters, labelled controls, full keyboard support.
- **Composable:** small parts that fit together, so you pick the level of abstraction for each screen.
- **Open code:** read it, change it, own it.
- **No lag:** values that change every frame, like playheads and meters, move without re-rendering your page.

## Next steps

Head over to [Get started](/docs/get-started) to add audio/ui to your project, or open [Knob](/docs/components/base/knob) to install your first component.
